import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useExpenses,
  useDeleteExpense,
  useApproveExpense,
  useRejectExpense,
  useDownloadReceipt,
} from '../../../hooks/useExpenses';
import { EXPENSE_STATUS } from '../../../utils/constants';
import { formatCurrency } from '../../../utils/formatters';
import { useCategories } from '../../../hooks/useCategories';
import { useToast } from '../../../hooks/useToast';
import ExpensesTable from '../../../components/tables/ExpensesTable';
import Loading from '../../../components/common/Loading';
import ErrorMessage from '../../../components/common/ErrorMessage';
import Button from '../../../components/common/Button';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import Pagination from '../../../components/common/Pagination';
import { Plus, Search, Filter } from 'lucide-react';

const ExpensesList = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    dateFrom: '',
    dateTo: '',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: null,
    id: null,
  });

  const { data, isLoading, error, refetch } = useExpenses({ ...filters, page });
  const { data: categories } = useCategories();
  const deleteExpense = useDeleteExpense();
  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();
  const downloadReceipt = useDownloadReceipt();
  const toast = useToast();

  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({
      search: '',
      status: '',
      category: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  const handleDelete = (id) => {
    setConfirmDialog({ isOpen: true, type: 'delete', id });
  };

  const handleApprove = (id) => {
    setConfirmDialog({ isOpen: true, type: 'approve', id });
  };

  const handleReject = (id) => {
    setConfirmDialog({ isOpen: true, type: 'reject', id });
  };

  const handleDownloadReceipt = async (id) => {
    try {
      await downloadReceipt.mutateAsync({ id });
    } catch (err) {
      toast.error('Erro ao baixar comprovante: ' + err.message);
    }
  };

  const closeDialog = () => {
    setConfirmDialog({ isOpen: false, type: null, id: null });
  };

  const handleConfirm = async (inputValue) => {
    const { type, id } = confirmDialog;
    closeDialog();

    try {
      if (type === 'delete') {
        await deleteExpense.mutateAsync(id);
        toast.success('Despesa excluída com sucesso!');
      } else if (type === 'approve') {
        await approveExpense.mutateAsync({ id, notes: 'Aprovado' });
        toast.success('Despesa aprovada com sucesso!');
      } else if (type === 'reject') {
        await rejectExpense.mutateAsync({ id, notes: inputValue });
        toast.success('Despesa rejeitada com sucesso!');
      }
    } catch (err) {
      const messages = {
        delete: 'Erro ao excluir despesa: ',
        approve: 'Erro ao aprovar despesa: ',
        reject: 'Erro ao rejeitar despesa: ',
      };
      toast.error((messages[type] || 'Erro: ') + err.message);
    }
  };

  const dialogProps = () => {
    const { type } = confirmDialog;
    if (type === 'delete') {
      return {
        title: 'Excluir despesa',
        message: 'Tem certeza que deseja excluir esta despesa?',
        confirmText: 'Excluir',
        variant: 'destructive',
      };
    }
    if (type === 'approve') {
      return {
        title: 'Aprovar despesa',
        message: 'Deseja aprovar esta despesa?',
        confirmText: 'Aprovar',
      };
    }
    if (type === 'reject') {
      return {
        title: 'Rejeitar despesa',
        message: 'Informe o motivo da rejeição:',
        confirmText: 'Rejeitar',
        variant: 'destructive',
        withInput: true,
        inputLabel: 'Motivo',
        inputPlaceholder: 'Motivo da rejeição...',
      };
    }
    return {};
  };

  if (isLoading) {
    return <Loading text="Carregando despesas..." />;
  }

  if (error) {
    return <ErrorMessage message={error.message} onRetry={refetch} />;
  }

  const expenses = data?.data || [];

  const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const approvedAmount = expenses
    .filter((e) => e.status === 'approved')
    .reduce((sum, expense) => sum + expense.amount, 0);
  const pendingAmount = expenses
    .filter((e) => e.status === 'pending')
    .reduce((sum, expense) => sum + expense.amount, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Despesas</h1>
          <p className="text-gray-600 mt-1">
            Gerencie as despesas da associação
          </p>
        </div>
        <Link to="/admin/despesas/nova">
          <Button variant="primary">
            <Plus className="h-5 w-5 mr-2" />
            Nova Despesa
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="label">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Descrição..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">Todos</option>
              <option value={EXPENSE_STATUS.PENDING}>Pendente</option>
              <option value={EXPENSE_STATUS.APPROVED}>Aprovado</option>
              <option value={EXPENSE_STATUS.REJECTED}>Rejeitado</option>
            </select>
          </div>

          <div>
            <label className="label">Categoria</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="input"
            >
              <option value="">Todas</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Data Inicial</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Data Final</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="input"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Total</div>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(totalAmount)}
          </div>
          <div className="text-xs text-gray-500 mt-1">{expenses.length} despesas</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Aprovado</div>
          <div className="text-3xl font-bold text-green-600">
            {formatCurrency(approvedAmount)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {expenses.filter((e) => e.status === 'approved').length} despesas
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Pendente</div>
          <div className="text-3xl font-bold text-yellow-600">
            {formatCurrency(pendingAmount)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {expenses.filter((e) => e.status === 'pending').length} despesas
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <ExpensesTable
          expenses={expenses}
          onDelete={handleDelete}
          onApprove={handleApprove}
          onReject={handleReject}
          onDownloadReceipt={handleDownloadReceipt}
        />
        <Pagination
          page={data?.page || 1}
          pageSize={data?.pageSize || 15}
          total={data?.total || 0}
          onPageChange={setPage}
        />
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={closeDialog}
        onConfirm={handleConfirm}
        {...dialogProps()}
      />
    </div>
  );
};

export default ExpensesList;
